import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {SearchType} from 'types/search';
import {BillingRulesThunks} from 'features/programs/program/billingRules/billingRulesThunks';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import {setActiveTab, PROGRAM_TABS} from 'features/programs/programsSlice';
import DotsMenu from 'components/DotsMenu';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import TrashIcon from 'assets/images/icons/TrashIcon';
import usePermissions from 'hooks/usePermissions';
import {setIsEditing} from '../billingSolution/billingSolutionSlice';
import {BillingRule} from 'types/billingRule';
import {setBillingRule} from './billingRulesSlice';
import {formatCentsToCurrency} from 'utils/currencyUtils';

const BillingRulesTable: React.FC<{openDrawer: () => void}> = ({
  openDrawer,
}) => {
  const billingRules = useSelector(
    (state: RootState) => state.billingRule.billingRules,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.billingRule.pagy);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const loading = useSelector(
    (state: RootState) => state.program.status === 'loading',
  );

  interface VisibleColumnsType {
    asOf: boolean;
    services: boolean;
    payers: boolean;
    locations: boolean;
    billingSolution: boolean;
    defaultRate: boolean;
  }

  const [visibleColumns, setVisibleColumns] =
    React.useState<VisibleColumnsType>({
      asOf: true,
      services: true,
      payers: true,
      locations: true,
      billingSolution: true,
      defaultRate: true,
    });

  const columnOptions = [
    {label: 'As Of', value: 'asOf', isVisible: visibleColumns.asOf},
    {label: 'Services', value: 'services', isVisible: visibleColumns.services},
    {label: 'Payers', value: 'payers', isVisible: visibleColumns.payers},
    {
      label: 'Locations',
      value: 'locations',
      isVisible: visibleColumns.locations,
    },
    {
      label: 'Billing Solution',
      value: 'billingSolution',
      isVisible: visibleColumns.billingSolution,
    },
    {
      label: 'Default Rate',
      value: 'defaultRate',
      isVisible: visibleColumns.defaultRate,
    },
  ];

  const searchByOptions = [{label: 'As Of', value: 'as_of', operator: '=='}];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    await dispatch(
      BillingRulesThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenNewBillingRule = () => {
    dispatch(
      setBillingRule({
        asOf: '',
        services: [],
        payers: [],
        locations: [],
        billingSolution: null,
        defaultRateInCents: 0,
      }),
    );
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const handleDelete = (id: string) => {
    setDotsMenuIsOpen(null);
    dispatch(BillingRulesThunks.delete(id));
  };

  const handleEdit = (id: string) => {
    dispatch(setIsEditing(true));
    openDrawer();
    dispatch(BillingRulesThunks.show(id));
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      activeTab={'Billing Rules'}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={PROGRAM_TABS}
      recordsName={'Billing Rules'}
      searchByOptions={searchByOptions}
      searchable={true}
      onColumnToggle={handleColumnToggle}
      columnOptions={columnOptions}
      loading={loading}
      columns={true}
      blueButton={
        checkPermission('create', 'BillingRule') ? (
          <BlueButton
            onClick={handleOpenNewBillingRule}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.asOf && <TableHeader label="As Of" sortBy="asOf" />}
          {visibleColumns.services && <TableHeader label="Services" />}
          {visibleColumns.payers && <TableHeader label="Payers" />}
          {visibleColumns.locations && <TableHeader label="Locations" />}
          {visibleColumns.billingSolution && (
            <TableHeader label="Billing Solution" />
          )}
          {visibleColumns.defaultRate && (
            <TableHeader label="Default Rate" sortBy="defaultRate" />
          )}
          <TableHeader label="" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {billingRules.map((billingRule: BillingRule) => {
          const userOptions = [];
          if (checkPermission('update', 'BillingRules')) {
            userOptions.push({
              label: 'Edit',
              onClick: () => handleEdit(billingRule.id),
              hoverClass: 'hover:bg-cyan-100',
            });
          }
          if (checkPermission('delete', 'BillingRules')) {
            userOptions.push({
              label: 'Delete',
              onClick: () => handleDelete(billingRule.id),
              icon: <TrashIcon />,
              hoverClass: 'hover:bg-red-100 hover:text-red-800',
            });
          }
          return (
            <TableRow key={billingRule.id}>
              {visibleColumns.asOf && (
                <TableCell>
                  {billingRule.asOf
                    ? new Date(billingRule.asOf).toLocaleDateString()
                    : '-'}
                </TableCell>
              )}
              {visibleColumns.services && (
                <TableCell>
                  {billingRule.services && billingRule.services.length > 0
                    ? billingRule.services
                        .map(service => service.name)
                        .join(', ')
                    : '-'}
                </TableCell>
              )}
              {visibleColumns.payers && (
                <TableCell>
                  {billingRule.payers && billingRule.payers.length > 0
                    ? billingRule.payers.map(payer => payer.name).join(', ')
                    : '-'}
                </TableCell>
              )}
              {visibleColumns.locations && (
                <TableCell>
                  {billingRule.locations && billingRule.locations.length > 0
                    ? billingRule.locations
                        .map(location => location.name)
                        .join(', ')
                    : '-'}
                </TableCell>
              )}
              {visibleColumns.billingSolution && (
                <TableCell>
                  {billingRule.billingSolution
                    ? billingRule.billingSolution.name
                    : '-'}
                </TableCell>
              )}
              {visibleColumns.defaultRate && (
                <TableCell>
                  {formatCentsToCurrency(billingRule.defaultRateInCents)}
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default BillingRulesTable;
